import {
  font,
  fontSize,
  neutral,
  primaryAction,
  radius,
  shadow,
  space,
  text as textToken,
} from './revampTokens';

/** Close control on white revamp modals (default Chakra close icon can read as invisible). */
export const revampModalCloseButtonStyles = {
  color: textToken.primary,
  _hover: { bg: neutral.surfaceHover, color: textToken.primary },
  _active: { bg: neutral.surfaceHover },
};

/** Space between modal body content and footer actions (avoids buttons sitting on the card border). */
export const revampModalFooterStyles = {
  p: 0,
  mt: space['3xl'],
  pt: space.xl,
  gap: space.lg,
  justifyContent: 'flex-end' as const,
  flexWrap: 'wrap' as const,
  borderTop: `1px solid ${neutral.border}`,
};

/** Shared label styles for revamp modals (PageModal, export/delivery). */
export const revampFieldLabelStyles = {
  fontFamily: font.body,
  fontWeight: 600,
  fontSize: '12px',
  color: textToken.primary,
  marginBottom: '4px',
};

/** Text inputs and textareas in revamp modals. */
export const revampFieldInputStyles = {
  fontFamily: font.body,
  fontSize: '14px',
  fontWeight: 500,
  color: textToken.primary,
  border: `1px solid ${neutral.border}`,
  borderRadius: radius.control,
  padding: '10px 12px',
  height: 'auto',
  boxShadow: 'none',
  _placeholder: { color: textToken.muted },
  _hover: { borderColor: neutral.borderStrong },
  _focusVisible: {
    borderColor: neutral.borderStrong,
    boxShadow: `0 0 0 1px ${neutral.borderStrong}`,
  },
};

/** Native selects in revamp modals. */
export const revampFieldSelectStyles = {
  ...revampFieldInputStyles,
  height: '40px',
  paddingRight: '32px',
  bg: neutral.surface,
  cursor: 'pointer',
};

/** Trigger for {@link RevampMenuSelect} — same inset as {@link revampFieldInputStyles}; chevron via `rightIcon`. */
export const revampMenuSelectButtonStyles = {
  ...revampFieldInputStyles,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  w: '100%',
  h: revampFieldSelectStyles.height,
  minH: revampFieldSelectStyles.height,
  height: revampFieldSelectStyles.height,
  /** Match inputs (`10px 12px`); do not use `variant="unstyled"` — it forces `p: 0`. */
  padding: '10px 12px',
  pl: '12px',
  pr: '10px',
  py: '10px',
  cursor: 'pointer',
  _hover: { bg: neutral.surface, borderColor: neutral.borderStrong },
  _active: { bg: neutral.surface },
  _expanded: {
    borderColor: neutral.borderStrong,
    boxShadow: `0 0 0 1px ${neutral.borderStrong}`,
  },
};

/** Dropdown panel for revamp menu selects (replaces unstyled native `<select>` popups). */
export const revampMenuListStyles = {
  py: 1,
  minW: 'unset',
  border: `1px solid ${neutral.border}`,
  borderRadius: radius.overlay,
  boxShadow: shadow.overlay,
  bg: neutral.surface,
  zIndex: 5100,
  maxH: '280px',
  overflowY: 'auto' as const,
  sx: {
    '& .chakra-menu__group__title': {
      fontFamily: font.body,
      fontSize: fontSize.label,
      fontWeight: 600,
      color: textToken.secondary,
      textTransform: 'none',
      letterSpacing: 'normal',
      px: 3,
      py: 2,
    },
    '& .chakra-menu__menuitem-option': {
      borderRadius: radius.control,
      mx: 1,
      px: 3,
      py: 2,
      minH: revampFieldSelectStyles.height,
      _focus: { bg: neutral.surfaceHover },
      _checked: { bg: neutral.surfaceHover, fontWeight: 600 },
    },
  },
};

/**
 * Modal footer actions — explicit padding (do not use `variant="unstyled"`; it sets `p: 0`
 * and wipes horizontal padding even when `px` is passed).
 */
const revampModalButtonBase = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  minW: 'fit-content',
  w: 'auto',
  h: 'auto',
  minH: revampFieldSelectStyles.height,
  padding: '10px 22px',
  lineHeight: '1.2',
  whiteSpace: 'nowrap' as const,
  textTransform: 'none' as const,
  boxShadow: 'none',
  borderRadius: radius.control,
  fontFamily: font.body,
  fontSize: fontSize.body,
  fontWeight: 500,
};

/** Secondary actions in revamp modals (Save, outline buttons). */
export const revampModalSecondaryButtonStyles = {
  ...revampModalButtonBase,
  color: textToken.primary,
  bg: neutral.surface,
  border: `1px solid ${neutral.border}`,
  _hover: { bg: neutral.surfaceHover },
};

/** Primary actions in revamp modals (Download, Test now). */
export const revampModalPrimaryButtonStyles = {
  ...revampModalButtonBase,
  color: textToken.inverse,
  bg: primaryAction.bg,
  border: 'none',
  _hover: { bg: primaryAction.hover },
};
